import { useCallback } from 'react';
import { type QueryKey, useQueryClient } from '@tanstack/react-query';

/** Ответ списка, элементы которого различаются идентификатором. */
type ItemsListResponse = { items: { id: number }[] };

/**
 * Чтение элемента из последнего полученного списка в момент вызова, а не на отрисовке.
 * Нужно записи, которая собирает содержимое из своей половины и чужой: отложенная запись или запись
 * вслед за другой взяла бы из отрисовки содержимое, которое та другая уже переписала.
 *
 * Ключ списка собирается фабрикой ключей сущности по параметру: фабрику держать стабильной
 * (метод объекта ключей), от неё и от параметра зависит возвращаемое чтение.
 */
export const useReadCachedListItem = <TList extends ItemsListResponse, TParam>(
  buildKey: (param: TParam) => QueryKey,
  param: TParam,
) => {
  const queryClient = useQueryClient();
  return useCallback(
    (id: number): TList['items'][number] | undefined =>
      queryClient.getQueryData<TList>(buildKey(param))?.items.find((item) => item.id === id),
    [queryClient, buildKey, param],
  );
};
